/*
 * App authentication (Part B: MSAL-based login and token exchange).
 * See docs/AUTH_UPGRADE_README.md Part B.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { createClient } from 'src/client';
import { Authenticated } from 'src/app/components';
import { StartScreen } from 'src/app/components';
import { PageSkeleton } from 'src/components';
import {
  getUrlDetails,
  getCurrentRoute,
  setAppUri,
  parseQueryString,
} from 'src/app/functions/core';
import { isLogoutValid, isExpired, getStoredLogoutCode } from 'src/app/functions/validation';
import {
  getRoute,
  setRoute,
  removeRoute,
  setAuth,
  removeAuth,
  getAuth,
  clearLogoutBroadcast,
} from 'src/app/functions/storage';
import { loginRequestScopes } from 'src/auth/msalConfig';
import { getAccessToken, setCachedAuthorization } from 'src/auth/authProvider';

/**
 * Get site name from a stored route path (e.g. "/asa.bne" -> "asa.bne").
 */
function getSiteFromStoredRoute(): string | null {
  const route = getRoute();
  if (!route) { return null; }
  const segments = route.split('/').filter(Boolean);
  return segments.length > 0 ? segments[0] : null;
}

export const AuthenticationContainer = ({ authFailed }: { authFailed?: boolean } = {}) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthFailed, setAuthFailed] = useState(false);
  const [authData, setAuthData] = useState<{
    authorization: string;
    refreshToken: string | null;
    refreshTokenExpiry: number;
  } | null>(null);
  const [tokenError, setTokenError] = useState(false);
  const [logoutCode, setLogoutCode] = useState<string | null>(null);

  const { siteName, pageRoute } = getUrlDetails();
  const isLogoutReturnPathRef = useRef(siteName === 'logout' || pageRoute === 'logout');
  const isLogoutReturnPath = isLogoutReturnPathRef.current;

  // Login handler: store route then MSAL redirect (Part B.1)
  const handleLogin = useCallback(() => {
    if (!siteName) { return; }
    setRoute(getCurrentRoute());
    instance.loginRedirect({ scopes: loginRequestScopes });
  }, [instance, siteName]);

  // Logout return path: /logout or /{siteName}/logout (Part B.3)
  useEffect(() => {
    if (!isLogoutReturnPath) { return; }

    // Read code BEFORE setAppUri which calls cleanupAfterComingBack (clears STATE from localStorage)
    setLogoutCode(getStoredLogoutCode());

    const query = parseQueryString(window.location.search.substring(1));
    const { validSiteName } = isLogoutValid(query.state);
    if (validSiteName) {
      setAppUri(validSiteName);
    } else if (siteName && siteName !== 'logout') {
      setAppUri(siteName);
    }
    removeAuth();
    clearLogoutBroadcast();
  }, [isLogoutReturnPath, siteName]);

  // When we have accounts, acquire token and sync to legacy storage (Part B.2)
  useEffect(() => {
    if (inProgress !== InteractionStatus.None || accounts.length === 0) { return; }
    if (authData) { return; } // already have token

    const account = accounts[0];
    instance
      .acquireTokenSilent({ scopes: loginRequestScopes, account })
      .then((result) => {
        const expiry = result.expiresOn ? result.expiresOn.getTime() : Date.now() + 3600 * 1000;
        setAuth({
          authorization: `Bearer ${result.accessToken}`,
          expiry,
          profile: result.idToken ?? null,
        });
        const authorization = `Bearer ${result.accessToken}`;
        setCachedAuthorization(authorization);
        setAuthData({
          authorization,
          refreshToken: null,
          refreshTokenExpiry: 0,
        });
        setTokenError(false);
      })
      .catch(() => {
        setTokenError(true);
        setAuthFailed(true);
      });
  }, [instance, accounts, inProgress, authData]);

  // Restore URL after successful login (replace ?code=... with stored route)
  useEffect(() => {
    if (!authData) { return; }
    const storedRoute = getRoute();
    if (storedRoute) {
      removeRoute();
      window.history.replaceState({}, '', storedRoute);
    }
  }, [authData]);

  // Show loading while MSAL is handling redirect or startup
  if (inProgress !== InteractionStatus.None) {
    return <PageSkeleton />;
  }

  // Have accounts but still acquiring token (avoid flashing StartScreen)
  if (accounts.length > 0 && !authData && !tokenError) {
    return <PageSkeleton />;
  }

  // Auth failed (e.g. token acquisition failed)
  if (isAuthFailed || tokenError) {
    removeAuth();
    return <StartScreen clickEvent={handleLogin} error="failed" />;
  }

  // Exception error, just say accessDenied
  if (authFailed) {
    return <StartScreen clickEvent={handleLogin} error="accessDenied" />;
  }

  // Authenticated: we have token and can render app (Part D: pass getToken for MSAL refresh)
  if (authData) {
    const client = createClient({
      getToken: () => getAccessToken(),
    });
    return <Authenticated client={client} token={authData.authorization} />;
  }

  // Logout return: show StartScreen (storage already cleared above)
  if (isLogoutReturnPath) {
    const effectiveCode = logoutCode;
    const signoutQuery = effectiveCode
      ? { text: effectiveCode, displayButton: effectiveCode !== '404' }
      : { text: 'welcome' as const, displayButton: false };
    return (
      <StartScreen
        clickEvent={handleLogin}
        text={signoutQuery.text}
        displayButton={signoutQuery.displayButton}
      />
    );
  }

  // Check for B2C error in URL (e.g. user cancelled)
  const query = parseQueryString(window.location.search.substring(1));
  if (query.error !== undefined && query.error_description !== undefined) {
    const site = getSiteFromStoredRoute();
    if (site) { setAppUri(site); }
    return <StartScreen clickEvent={handleLogin} error="accessDenied" />;
  }

  // Check for existing session in legacy storage (page refresh; only when no MSAL accounts)
  if (accounts.length === 0) {
    const existingAuth = getAuth();
    if (existingAuth?.authorization && existingAuth?.expiry && !isExpired(existingAuth.expiry)) {
      setCachedAuthorization(existingAuth.authorization);
      const client = createClient({
        authorization: existingAuth.authorization,
        refreshToken: null,
        refreshTokenExpiry: 0,
      });
      return (
        <Authenticated client={client} token={existingAuth.authorization} />
      );
    }
  }

  // Not logged in: show StartScreen
  return (
    <StartScreen
      clickEvent={handleLogin}
      text="welcome"
      error=""
      displayButton={!!siteName}
    />
  );
};
